import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Grid3x3, Table as TableIcon, FileText, Search, ShoppingBag, Minus, X } from 'lucide-react';
import { useAlert } from '../../../../components/AlertProvider';
import api from '../../../../services/api';
import { PurchaseOrder, ClientPricing, OrderConsumable, OrderCartItem } from './types';

interface OrdersTabProps {
  orders: PurchaseOrder[];
  clientId: number;
  onReload: () => void;
  isCreateOpen: boolean;
  onCreateOpenChange: (next: boolean) => void;
  pricing?: ClientPricing[];
}

export const OrdersTab: React.FC<OrdersTabProps> = ({ orders, clientId, onReload, isCreateOpen, onCreateOpenChange, pricing = [] }) => {
  const navigate = useNavigate();
  const { showError, showSuccess } = useAlert();

  const [catalog, setCatalog] = useState<OrderConsumable[]>([]);
  const [catalogLoading, setCatalogLoading] = useState<boolean>(false);
  const [productSearch, setProductSearch] = useState<string>('');
  const [cart, setCart] = useState<OrderCartItem[]>([]);
  const [notes, setNotes] = useState<string>('');
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [viewMode, setViewMode] = useState<'card' | 'table' | 'list'>('table');
  const [selectedOrder, setSelectedOrder] = useState<PurchaseOrder | null>(null);

  const closeCreateModal = (): void => {
    onCreateOpenChange(false);
  };

  const loadCatalog = async (): Promise<void> => {
    setCatalogLoading(true);
    try {
      const res = await api.get('/consumables');
      const list = Array.isArray(res.data) ? (res.data as OrderConsumable[]) : [];
      setCatalog(list);
    } catch {
      setCatalog([]);
      showError('Erreur lors du chargement du catalogue');
    } finally {
      setCatalogLoading(false);
    }
  };

  useEffect(() => {
    if (isCreateOpen && catalog.length === 0 && !catalogLoading) {
      void loadCatalog();
    }
  }, [isCreateOpen]);

  const addToCart = (consumable: OrderConsumable): void => {
    setCart((prev) => {
      const existing = prev.find((i) => i.consumable.id === consumable.id);
      if (existing) {
        return prev.map((i) =>
          i.consumable.id === consumable.id ? { ...i, quantity: i.quantity + 1 } : i
        );
      }
      return [...prev, { consumable, quantity: 1 }];
    });
  };

  const updateQuantity = (consumableId: number, delta: number): void => {
    setCart((prev) =>
      prev
        .map((i) => {
          if (i.consumable.id !== consumableId) return i;
          return { ...i, quantity: Math.max(0, i.quantity + delta) };
        })
        .filter((i) => i.quantity > 0)
    );
  };

  const getClientPrice = (consumable: OrderConsumable): number => {
    // Find active BPU: only validated ones
    const activeBpu = pricing.find(bpu =>
      bpu.status === 'validated' && bpu.is_active
    );

    if (!activeBpu?.pricing_items) return consumable.base_price;

    const item = activeBpu.pricing_items.find(i => i.consumable_id === consumable.id);
    return item ? item.custom_price : consumable.base_price;
  };

  const getEstimatedTotal = (): number => {
    return cart.reduce((sum, i) => sum + i.quantity * getClientPrice(i.consumable), 0);
  };

  const submitOrder = async (): Promise<void> => {
    if (cart.length === 0) {
      showError('Veuillez ajouter au moins un article');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        client_id: clientId,
        notes: notes.trim() || undefined,
        items: cart.map((i) => ({
          consumable_id: i.consumable.id,
          quantity: i.quantity,
        })),
      };

      const res = await api.post('/purchase-orders', payload);
      const orderNumber = res.data?.order_number as string | undefined;
      showSuccess(orderNumber ? `Commande ${orderNumber} créée avec succès` : 'Commande créée avec succès');

      closeCreateModal();
      setCart([]);
      setNotes('');
      onReload();
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      showError(typeof detail === 'string' ? detail : 'Erreur lors de la création de la commande');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { label: string; className: string }> = {
      draft: { label: 'Brouillon', className: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300' },
      pending: { label: 'En attente', className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300' },
      confirmed: { label: 'Confirmée', className: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' },
      processing: { label: 'En cours', className: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300' },
      shipped: { label: 'Expédiée', className: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300' },
      delivered: { label: 'Livrée', className: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' },
      cancelled: { label: 'Annulée', className: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' },
    };

    const config = statusConfig[status] || { label: status, className: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300' };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.className}`}>
        {config.label}
      </span>
    );
  };

  return (
    <div className="bg-white dark:bg-slate-900 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700">
      <div className="p-3 sm:p-6">
        <div className="flex flex-col gap-3 sm:gap-4 md:flex-row md:items-center md:justify-between mb-4 sm:mb-6">
          <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-slate-100">Historique des commandes</h2>
          <div className="flex items-center gap-2 sm:gap-3">
            {orders.length > 0 && (
              <div className="hidden sm:flex items-center gap-1 border border-slate-300 dark:border-slate-600 rounded-lg p-1">
                <button
                  type="button"
                  onClick={() => setViewMode('card')}
                  className={`p-2 rounded flex items-center justify-center ${viewMode === 'card' ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400' : 'text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'}`}
                >
                  <Grid3x3 className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode('table')}
                  className={`p-2 rounded flex items-center justify-center ${viewMode === 'table' ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400' : 'text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'}`}
                >
                  <TableIcon className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
        </div>

        {orders.length === 0 ? (
          <div className="text-center py-8 sm:py-12">
            <FileText className="w-10 h-10 sm:w-12 sm:h-12 text-gray-400 dark:text-slate-600 mx-auto mb-3 sm:mb-4" />
            <p className="text-sm sm:text-base text-gray-500 dark:text-slate-400">Aucune commande enregistrée</p>
          </div>
        ) : viewMode === 'table' && typeof window !== 'undefined' && window.innerWidth >= 640 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-slate-700">
              <thead className="bg-gray-50 dark:bg-slate-800">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                    N° Commande
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                    Montant
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                    Livraison prévue
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-slate-900 divide-y divide-gray-200 dark:divide-slate-700">
                {orders.map((order) => (
                  <tr
                    key={order.id}
                    className="hover:bg-gray-50 dark:hover:bg-slate-800 cursor-pointer transition-colors"
                    onClick={() => setSelectedOrder(order)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-slate-100">
                      {order.order_number}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-slate-400">
                      {new Date(order.created_at).toLocaleDateString('fr-FR')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(order.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-slate-100">
                      {order.total_amount.toFixed(2)} €
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-slate-400">
                      {order.delivery_date ? new Date(order.delivery_date).toLocaleDateString('fr-FR') : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4">
            {orders.map((order) => (
              <div
                key={order.id}
                onClick={() => setSelectedOrder(order)}
                className="bg-white dark:bg-slate-900 rounded-lg sm:rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm p-3 sm:p-4 flex flex-col gap-2 sm:gap-3 cursor-pointer hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="text-xs text-slate-500 dark:text-slate-400 mb-1">Commande</div>
                    <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">{order.order_number}</div>
                    <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                      {new Date(order.created_at).toLocaleDateString('fr-FR')}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    {getStatusBadge(order.status)}
                    <div className="text-sm font-semibold text-emerald-700 dark:text-emerald-400">
                      {order.total_amount.toFixed(2)} €
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400 border-t border-slate-100 dark:border-slate-700 pt-2">
                  <span>Livraison prévue</span>
                  <span className="font-medium text-slate-700 dark:text-slate-300">
                    {order.delivery_date ? new Date(order.delivery_date).toLocaleDateString('fr-FR') : '-'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Order Creation Modal */}
      {isCreateOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4 animate-in fade-in duration-200">
          <div className="bg-white dark:bg-slate-900 rounded-xl shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-3 sm:p-6 border-b border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900">
              <div>
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-slate-100">Nouvelle commande</h3>
                <p className="text-sm text-gray-500 dark:text-slate-400">Client #{clientId}</p>
              </div>
              <button onClick={closeCreateModal} className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-0 min-h-0 flex-1 overflow-hidden">
              {/* Catalog */}
              <div className="lg:col-span-2 p-3 sm:p-6 border-b lg:border-b-0 lg:border-r border-gray-200 dark:border-slate-700 overflow-y-auto bg-gray-50/50 dark:bg-slate-900/50">
                <div className="flex items-center gap-3 mb-4 sticky top-0 bg-gray-50/50 dark:bg-slate-900/50 z-10 pb-2">
                  <div className="relative flex-1">
                    <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      value={productSearch}
                      onChange={(e) => setProductSearch(e.target.value)}
                      placeholder="Rechercher un produit..."
                      className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white dark:bg-slate-800 text-gray-900 dark:text-slate-100 placeholder-gray-400 dark:placeholder-slate-500"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => void loadCatalog()}
                    className="px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-800 text-gray-700 dark:text-slate-300 transition-colors"
                    disabled={catalogLoading}
                  >
                    {catalogLoading ? 'Chargement...' : 'Rafraîchir'}
                  </button>
                </div>

                {catalogLoading && catalog.length === 0 ? (
                  <div className="text-center py-12 text-gray-500 dark:text-slate-400">Chargement du catalogue...</div>
                ) : (
                  <div className="space-y-2">
                    {catalog
                      .filter((p) => {
                        const q = productSearch.trim().toLowerCase();
                        if (!q) return true;
                        return p.title.toLowerCase().includes(q);
                      })
                      .slice(0, 50)
                      .map((p) => (
                        <div
                          key={p.id}
                          className="flex items-center justify-between p-3 border border-gray-200 dark:border-slate-700 rounded-lg hover:bg-white dark:hover:bg-slate-800 bg-white dark:bg-slate-800/50 transition-colors shadow-sm"
                        >
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-gray-900 dark:text-slate-100 truncate">{p.title}</div>
                            {(() => {
                              const price = getClientPrice(p);
                              const isDiscounted = price < p.base_price;
                              return (
                                <div className="text-xs text-gray-500 dark:text-slate-400">
                                  {isDiscounted ? (
                                    <>
                                      <span className="line-through mr-2">{p.base_price.toFixed(2)} €</span>
                                      <span className="text-emerald-600 font-bold">{price.toFixed(2)} €</span>
                                    </>
                                  ) : (
                                    <span>{p.base_price.toFixed(2)} €</span>
                                  )}
                                  <span> / {p.unit}</span>
                                </div>
                              );
                            })()}
                          </div>
                          <button
                            type="button"
                            onClick={() => addToCart(p)}
                            className="ml-3 inline-flex items-center px-3 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors shadow-sm"
                          >
                            <Plus className="w-4 h-4 mr-1" />
                            Ajouter
                          </button>
                        </div>
                      ))}
                  </div>
                )}
              </div>

              {/* Cart */}
              <div className="p-3 sm:p-6 overflow-y-auto bg-white dark:bg-slate-900 flex flex-col h-full">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-slate-100 mb-4 flex items-center gap-2">
                  <ShoppingBag className="w-4 h-4" />
                  Panier
                </h4>

                {cart.length === 0 ? (
                  <div className="text-sm text-gray-500 dark:text-slate-400 text-center py-8 border-2 border-dashed border-gray-200 dark:border-slate-700 rounded-xl">
                    Aucun article dans le panier.
                  </div>
                ) : (
                  <div className="space-y-3 flex-1 overflow-y-auto min-h-0">
                    {cart.map((i) => (
                      <div key={i.consumable.id} className="flex items-center justify-between border border-gray-200 dark:border-slate-700 rounded-lg p-3 bg-gray-50 dark:bg-slate-800/50">
                        <div className="min-w-0 flex-1 mr-3">
                          <div className="text-sm font-medium text-gray-900 dark:text-slate-100 truncate">{i.consumable.title}</div>
                          {(() => {
                            const price = getClientPrice(i.consumable);
                            const isDiscounted = price < i.consumable.base_price;
                            return (
                              <div className="text-xs text-gray-500 dark:text-slate-400">
                                {isDiscounted ? (
                                  <>
                                    <span className="line-through mr-2">{i.consumable.base_price.toFixed(2)} €</span>
                                    <span className="text-emerald-600 font-bold">{price.toFixed(2)} €</span>
                                  </>
                                ) : (
                                  <span>{i.consumable.base_price.toFixed(2)} €</span>
                                )}
                                <span> / {i.consumable.unit}</span>
                              </div>
                            );
                          })()}
                        </div>
                        <div className="flex items-center gap-2 bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 p-1">
                          <button
                            type="button"
                            onClick={() => updateQuantity(i.consumable.id, -1)}
                            className="p-1 hover:bg-gray-100 dark:hover:bg-slate-700 rounded transition-colors text-gray-600 dark:text-slate-400"
                            aria-label="Diminuer"
                          >
                            <Minus className="w-3.5 h-3.5" />
                          </button>
                          <div className="w-8 text-center text-sm font-medium text-gray-900 dark:text-slate-100">{i.quantity}</div>
                          <button
                            type="button"
                            onClick={() => updateQuantity(i.consumable.id, 1)}
                            className="p-1 hover:bg-gray-100 dark:hover:bg-slate-700 rounded transition-colors text-gray-600 dark:text-slate-400"
                            aria-label="Augmenter"
                          >
                            <Plus className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="mt-6 pt-4 border-t border-gray-200 dark:border-slate-700 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Notes</label>
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white dark:bg-slate-800 text-gray-900 dark:text-slate-100 placeholder-gray-400 dark:placeholder-slate-500 resize-none"
                      rows={2}
                      placeholder="Notes internes (optionnel)"
                    />
                  </div>

                  <div className="flex items-center justify-between text-sm bg-emerald-50 dark:bg-emerald-900/20 p-3 rounded-lg border border-emerald-100 dark:border-emerald-900/30">
                    <span className="text-emerald-800 dark:text-emerald-300 font-medium">Total estimé (base)</span>
                    <span className="font-bold text-emerald-900 dark:text-emerald-200 text-lg">{getEstimatedTotal().toFixed(2)} €</span>
                  </div>
                  <p className="text-xs text-gray-400 dark:text-slate-500 text-center">Le total final sera calculé selon le BPU au moment de la création.</p>

                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={closeCreateModal}
                      className="flex-1 px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-800 text-gray-700 dark:text-slate-300 transition-colors"
                      disabled={submitting}
                    >
                      Annuler
                    </button>
                    <button
                      type="button"
                      onClick={() => void submitOrder()}
                      className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium shadow-sm"
                      disabled={submitting || cart.length === 0}
                    >
                      {submitting ? 'Création...' : 'Créer la commande'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Order Detail Modal */}
      {selectedOrder && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4 animate-in fade-in duration-200">
          <div className="bg-white dark:bg-slate-900 rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-3 sm:p-6 border-b border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900">
              <div>
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-slate-100">Détails de la commande</h3>
                <p className="text-sm text-gray-500 dark:text-slate-400">{selectedOrder.order_number}</p>
              </div>
              <button onClick={() => setSelectedOrder(null)} className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-3 sm:p-6 overflow-y-auto min-h-0">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
                <div>
                  <h4 className="text-sm font-medium text-gray-500 dark:text-slate-400 mb-2">Informations générales</h4>
                  <dl className="space-y-2">
                    <div className="flex justify-between">
                      <dt className="text-sm text-gray-600 dark:text-slate-400">Numéro:</dt>
                      <dd className="text-sm font-medium text-gray-900 dark:text-slate-100">{selectedOrder.order_number}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-sm text-gray-600 dark:text-slate-400">Date:</dt>
                      <dd className="text-sm font-medium text-gray-900 dark:text-slate-100">
                        {new Date(selectedOrder.created_at).toLocaleDateString('fr-FR')}
                      </dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-sm text-gray-600 dark:text-slate-400">Statut:</dt>
                      <dd>{getStatusBadge(selectedOrder.status)}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-sm text-gray-600 dark:text-slate-400">Montant total:</dt>
                      <dd className="text-sm font-medium text-gray-900 dark:text-slate-100">
                        {selectedOrder.total_amount.toFixed(2)} €
                      </dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-sm text-gray-600 dark:text-slate-400">Date de livraison:</dt>
                      <dd className="text-sm font-medium text-gray-900 dark:text-slate-100">
                        {selectedOrder.delivery_date ? new Date(selectedOrder.delivery_date).toLocaleDateString('fr-FR') : '-'}
                      </dd>
                    </div>
                  </dl>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-gray-500 dark:text-slate-400 mb-2">Actions</h4>
                  <div className="space-y-2">
                    <button
                      onClick={() => {
                        navigate(`/achat/orders/${selectedOrder.id}`);
                        setSelectedOrder(null);
                      }}
                      className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Voir la commande complète
                    </button>
                    <button
                      onClick={() => setSelectedOrder(null)}
                      className="w-full px-4 py-2 text-gray-700 dark:text-slate-200 bg-gray-100 dark:bg-slate-800 rounded-lg hover:bg-gray-200 dark:hover:bg-slate-700 transition-colors"
                    >
                      Fermer
                    </button>
                  </div>
                </div>
              </div>

              <div className="border-t border-gray-200 dark:border-slate-700 pt-4">
                <h4 className="text-sm font-medium text-gray-500 dark:text-slate-400 mb-3">Produits commandés</h4>
                <div className="bg-gray-50 dark:bg-slate-800/50 rounded-lg p-4">
                  <p className="text-sm text-gray-600 dark:text-slate-400 text-center">
                    Les détails des produits seront chargés ici...
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
